import { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Paper } from '@shared/ui/common/paper';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useConnections } from '@shared/hooks';
import { useImpersonation } from '@shared/hooks/useImpersonation';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useBolSync } from '../hooks/useBolConnector';
import { SearchTermSync } from './SearchTermSync';
import ConnectionActiveImports from '../../generic/components/ConnectionActiveImports';
import { ACTIVE_IMPORT_JOBS_QUERY_KEY } from '../../hooks/useActiveImportJobs';
import { APP_CONFIG } from '../../../../config/app.config';
import { useToastStore } from '../../../../application/store/toastStore';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';

interface BolConnectionSettingsProps {
    connectionId: string;
}

const BolConnectionSettings = ({ connectionId }: BolConnectionSettingsProps) => {
    const { onClose } = useGenericDrawer();
    const queryClient = useQueryClient();
    const { addToast } = useToastStore();
    const { canImpersonate } = useImpersonation();
    const { connections, removeConnection } = useConnections();
    const connection = connections?.find(c => c.id === connectionId);

    const {
        syncAllOrders,
        syncNewOrders,
        syncOffers,
        syncInventory,
        syncRanks,
        syncSearchTerms,
        syncInvoices,
    } = useBolSync();
    const addJob = useImportJobsStore((s) => s.addJob);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [searchTermDialogOpen, setSearchTermDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeSync, setActiveSync] = useState<string | null>(null);

    const syncActions: { key: string; label: string; run: (connectionId: string) => Promise<{ jobId: string }> }[] = [
        { key: 'orders-new', label: 'Sync New Orders', run: syncNewOrders },
        { key: 'orders-all', label: 'Sync All Orders', run: syncAllOrders },
        { key: 'offers', label: 'Sync Offers', run: syncOffers },
        { key: 'inventory', label: 'Sync Inventory', run: syncInventory },
        { key: 'ranks', label: 'Sync Product Ranks', run: syncRanks },
        { key: 'search-terms', label: 'Sync Search Terms', run: syncSearchTerms },
        { key: 'invoices', label: 'Sync Invoice Specification', run: syncInvoices },
    ];

    const handleSync = async (action: typeof syncActions[number]) => {
        setActiveSync(action.key);
        try {
            const { jobId } = await action.run(connectionId);
            addJob({
                id: jobId,
                connector: 'bol',
                label: `${connection?.name ?? 'Bol'} · ${action.label.replace(/^Sync /, '')}`,
            });
            await queryClient.invalidateQueries({ queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY });
            addToast(`${action.label} started`, 'success');
        } catch {
            addToast(`${action.label} failed`, 'error');
        } finally {
            setActiveSync(null);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await removeConnection(connectionId);
            addToast('Connection deleted', 'success');
            onClose();
        } catch {
            addToast('Failed to delete connection', 'error');
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Stack
                direction="row"
                position="sticky"
                top={0}
                p={4}
                zIndex={1}
                bgcolor="background.paper"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    {connection && (
                        <Avatar
                            variant="rounded"
                            src={connection.connector.image}
                            alt={connection.connector.type}
                            sx={{ width: 36, height: 36, border: 1, borderColor: 'divider' }}
                            slotProps={{ img: { style: { objectFit: 'contain' } } }}
                        />
                    )}
                    <Typography variant="h6">{connection?.name ?? 'Bol.com Settings'}</Typography>
                </Stack>
                {onClose && (
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                )}
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 4, pb: 4 }}>
                {canImpersonate && (
                    <Paper>
                        <DialogHeader>
                            <Typography variant="h6">Sync</Typography>
                        </DialogHeader>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Syncs are queued in the background. Track progress below.
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {syncActions.map(action => (
                                    <Button
                                        key={action.key}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleSync(action)}
                                        disabled={activeSync !== null}
                                        startIcon={activeSync === action.key
                                            ? <CircularProgress size={14} color="inherit" />
                                            : undefined}
                                    >
                                        {activeSync === action.key ? 'Starting…' : action.label}
                                    </Button>
                                ))}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSearchTermDialogOpen(true)}
                                    disabled={activeSync !== null}
                                >
                                    Sync Search Term
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>
                )}

                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Active Imports</Typography>
                    </DialogHeader>
                    <ConnectionActiveImports connectionId={connectionId} />
                </Paper>

                <Paper>
                    <DialogHeader>
                        <Typography variant="h6" color="error.main">Danger Zone</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Deleting this connection will remove all associated listings and data. This action cannot be undone.
                            </Typography>
                            <Box>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete Connection
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            <SearchTermSync
                open={searchTermDialogOpen}
                onClose={() => setSearchTermDialogOpen(false)}
                connectionId={connectionId}
                connectionName={connection?.name ?? 'Bol'}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Confirm Delete</Typography>
                    <IconButton onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 3 }}>
                    <Typography>
                        Are you sure you want to delete <strong>{connection?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BolConnectionSettings;
